import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Button from "../../components/common/ui/button/Button";
import {
  Download,
  FileText,
  Calendar,
  Hash,
  Lock,
  CheckCircle,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { downloadFromResponse } from "../../utils/fileDownload";

export interface FileInfo {
  filename: string;
  fileType: string;
  fileSize: number;
  sharedBy: string;
  expiresAt: string | null;
  maxDownloads: number | null;
  remainingDownloads: number | null;
}

export const PublicFileDisplay: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);

  useEffect(() => {
    loadFileInfo();
  }, [token]);

  const loadFileInfo = async () => {
    try {
      const data = await apiClient.getShareLinkInfo(token!);

      if (data.resourceType !== "file") {
        setError("This link is not for a file");
        setLoading(false);
        return;
      }

      setFileInfo({
        filename: data.metadata?.filename || "Shared File",
        fileType: data.metadata?.fileType || "application/octet-stream",
        fileSize: data.metadata?.fileSize || 0,
        sharedBy: data.sharedBy || "Unknown",
        expiresAt: data.expiresAt,
        maxDownloads: data.maxDownloads,
        remainingDownloads: data.remainingDownloads,
      });
      setLoading(false);
    } catch (err: any) {
      if (err.message.includes("404")) {
        setError("This share link does not exist or has expired");
      } else {
        setError(err.message || "Failed to load share link");
      }
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!token) return;

    setDownloading(true);
    try {
      const response = await apiClient.downloadShareLinkFile(token);
      await downloadFromResponse(response, fileInfo?.filename || "download");
      setDownloadComplete(true);
    } catch (err: unknown) {
      console.error("Download error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to download file";
      setError(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatExpiryDate = (dateString: string | null): string => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return "Expired";
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `in ${days} days`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (downloadComplete) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Download Complete
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {fileInfo?.filename || "Your file"} has been downloaded
              successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !fileInfo) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {error || "File not found"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full px-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto w-20 h-20 bg-brand-100 dark:bg-brand-900/20 rounded-full flex items-center justify-center mb-4">
            <FileText className="h-10 w-10 text-brand-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {fileInfo.sharedBy} shared a file with you
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Click the button below to download
          </p>
        </div>

        {/* File Info Card */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <FileText className="h-6 w-6 text-brand-600 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">
                {fileInfo.filename}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatFileSize(fileInfo.fileSize)}
              </p>
            </div>
          </div>

          {/* Metadata */}
          {(fileInfo.expiresAt || fileInfo.maxDownloads) && (
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {fileInfo.expiresAt && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Expires {formatExpiryDate(fileInfo.expiresAt)}
                  </span>
                </div>
              )}
              {fileInfo.maxDownloads && (
                <div className="flex items-center gap-3 text-sm">
                  <Hash className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {fileInfo.remainingDownloads} of {fileInfo.maxDownloads}{" "}
                    downloads remaining
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Download Button */}
        <Button
          variant="primary"
          size="md"
          className="w-full"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={!downloading && <Download className="h-5 w-5" />}
        >
          {downloading ? "Downloading..." : "Download File"}
        </Button>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
          <p className="flex items-center justify-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            Secure file sharing
          </p>
        </div>
      </div>
    </div>
  );
};
